import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Camera, Image, CheckCircle, AlertCircle, Loader, Play } from 'lucide-react';
import './PhotoUploader.css';
import { getApiUrl } from '../utils/config';
import authService from '../services/authService';

const PhotoUploader = ({ albumId, onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadResults, setUploadResults] = useState({ success: [], errors: [] });
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [processProgress, setProcessProgress] = useState(0);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    processFiles(selectedFiles);
  };

  const processFiles = (selectedFiles) => {
    const validFiles = selectedFiles.filter(file => {
      // Accept both images and videos - NO SIZE LIMIT
      const validTypes = [
        // Images
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff',
        // Videos
        'video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv',
        'video/webm', 'video/3gpp', 'video/x-flv', 'video/x-m4v'
      ];
      
      if (!validTypes.includes(file.type.toLowerCase())) {
        alert(`${file.name} is not a supported file type. Please upload images or videos.`);
        return false;
      }
      
      return true;
    });

    // Add preview URLs to files
    const filesWithPreviews = validFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: URL.createObjectURL(file),
      status: 'pending',
      isVideo: file.type.startsWith('video/')
    }));

    setFiles(prev => [...prev, ...filesWithPreviews]);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      processFiles(droppedFiles);
      e.dataTransfer.clearData();
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const removeFile = (fileId) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  // FIXED: Enhanced uploadFiles function with correct route
  const uploadFiles = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setProcessingComplete(false);
    setProcessProgress(0);

    const formData = new FormData();
    
    // Add files to form data (the route expects 'photos' field name)
    files.forEach(({ file }) => {
      formData.append('photos', file);
    });

    // Note: We don't append albumId to formData since it's in the URL path

    try {
      // Update file statuses to uploading
      setFiles(prev => prev.map(file => ({ ...file, status: 'uploading' })));

      // FIXED: Use the correct route with albumId in the path and proper authentication
      const response = await authService.authenticatedFetch(`${getApiUrl()}/api/upload/${albumId}`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Upload response:', result);

        // Mark files as uploaded
        setFiles(prev => prev.map(file => ({ ...file, status: 'success' })));
        setUploadResults(result);

        // Start processing phase with progress indicator
        console.log('Files uploaded, starting B2 processing phase...');
        await simulateProcessingProgress();
        
        // Mark processing as complete
        setProcessingComplete(true);
        
        // Call completion callback after processing
        if (onUploadComplete) {
          onUploadComplete();
        }

      } else {
        const errorData = await response.json();
        console.error('Upload failed:', errorData);
        
        // Mark files as failed
        setFiles(prev => prev.map(file => ({ 
          ...file, 
          status: 'error',
          error: errorData.error || 'Upload failed'
        })));
      }
    } catch (error) {
      console.error('Upload error:', error);
      setFiles(prev => prev.map(file => ({ 
        ...file, 
        status: 'error',
        error: 'Network error occurred'
      })));
    } finally {
      setUploading(false);
    }
  };

  // Function to simulate processing progress
  const simulateProcessingProgress = async () => {
    const totalTime = 8000; // 8 seconds total
    const intervals = 20; // Update every 400ms
    const increment = 100 / intervals;
    
    for (let i = 0; i <= intervals; i++) {
      setProcessProgress(Math.min(i * increment, 100));
      if (i < intervals) {
        await new Promise(resolve => setTimeout(resolve, totalTime / intervals));
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const clearAll = () => {
    files.forEach(({ preview }) => {
      if (preview) URL.revokeObjectURL(preview);
    });
    setFiles([]);
    setUploadResults({ success: [], errors: [] });
    setProcessingComplete(false);
    setProcessProgress(0);
  };

  return (
    <div className="uploader-overlay">
      <div className="uploader-modal">
        <div className="uploader-header">
          <h2>
            <Upload size={24} />
            Upload Media
          </h2>
          <button className="btn-close" onClick={onClose} disabled={uploading}>
            <X size={24} />
          </button>
        </div>

        <div className="uploader-content">
          {files.length === 0 ? (
            <div 
              className="drop-zone"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="drop-zone-content">
                <Upload size={48} className="drop-zone-icon" />
                <h3>Drop files here or click to browse</h3>
                <p>Upload photos and videos of any size</p>
              </div>
            </div>
          ) : (
            <div className="file-list">
              <div className="file-list-header">
                <h4>{files.length} file(s) selected</h4>
                <div className="file-actions">
                  {!processingComplete && (
                    <button onClick={clearAll} className="btn-clear" disabled={uploading}>
                      Clear All
                    </button>
                  )}
                  {!uploading && !processingComplete && (
                    <button onClick={uploadFiles} className="btn-upload" disabled={files.length === 0}>
                      <Upload size={18} />
                      Upload {files.length} {files.length === 1 ? 'File' : 'Files'}
                    </button>
                  )}
                  {processingComplete && (
                    <button className="btn-done" onClick={onClose}>
                      Done
                    </button>
                  )}
                </div>
              </div>

              {/* Processing Progress Bar */}
              {uploading && processProgress > 0 && (
                <div className="processing-section">
                  <h3>Processing Photos...</h3>
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar" 
                      style={{ width: `${processProgress}%` }}
                    />
                  </div>
                  <p className="progress-text">
                    {processProgress < 100 
                      ? `Processing photos and generating thumbnails... ${Math.round(processProgress)}%`
                      : 'Almost ready! Finalizing upload...'
                    }
                  </p>
                </div>
              )}

              {/* Success Message */}
              {processingComplete && (
                <div className="upload-complete">
                  <CheckCircle size={48} className="success-icon" />
                  <h3>Upload Complete!</h3>
                  <p>Your photos have been successfully uploaded and processed.</p>
                </div>
              )}

              <div className="file-grid">
                {files.map((fileData) => (
                  <div key={fileData.id} className={`file-item ${fileData.status}`}>
                    <div className="file-preview">
                      {fileData.isVideo ? (
                        <div className="video-preview">
                          <video src={fileData.preview} className="preview-media" />
                          <div className="video-overlay">
                            <Play size={32} />
                          </div>
                        </div>
                      ) : (
                        <img src={fileData.preview} alt={fileData.file.name} className="preview-media" />
                      )}
                      
                      {!uploading && !processingComplete && (
                        <button 
                          className="btn-remove" 
                          onClick={() => removeFile(fileData.id)}
                          title="Remove file"
                        >
                          <X size={16} />
                        </button>
                      )}

                      <div className="file-status">
                        {fileData.status === 'pending' && <div className="status-pending" />}
                        {fileData.status === 'uploading' && <Loader size={16} className="spinner" />}
                        {fileData.status === 'success' && <CheckCircle size={16} className="status-success" />}
                        {fileData.status === 'error' && <AlertCircle size={16} className="status-error" />}
                      </div>
                    </div>
                    
                    <div className="file-info">
                      <p className="file-name">{fileData.file.name}</p>
                      <p className="file-size">{formatFileSize(fileData.file.size)}</p>
                      {fileData.error && (
                        <p className="file-error">{fileData.error}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Actions */}
          <div className="upload-actions">
            {!processingComplete && (
              <>
                <button 
                  className="btn-select"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Image size={20} />
                  Browse Files
                </button>
                
                <button 
                  className="btn-camera"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Camera size={20} />
                  Take Photo
                </button>
              </>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
  );
};

export default PhotoUploader;